import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import { getAuthErrorMessage } from '@utils/authErrors';
import { isAdult, MINIMUM_AGE } from '@utils/age';
import PageBackground from '@components/PageBackground';
import SeoHead from '@components/SeoHead';

type UserRole = 'roommate_seeker' | 'landlord';

const VALID_ROLES = new Set<UserRole>(['roommate_seeker', 'landlord']);

const parseRole = (value: string | null): UserRole =>
  VALID_ROLES.has(value as UserRole) ? (value as UserRole) : 'roommate_seeker';

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signup, signupWithGoogle } = useAuth();

  const [role, setRole] = useState<UserRole>(parseRole(searchParams.get('role')));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!dateOfBirth) {
      setError('Please enter your date of birth.');
      return;
    }
    if (!isAdult(dateOfBirth)) {
      setError(`You must be at least ${MINIMUM_AGE} years old to use RoomZa.`);
      return;
    }
    if (!acceptedTerms) {
      setError('Please accept the Terms of Use and Community Guidelines to continue.');
      return;
    }

    setLoading(true);

    try {
      await signup(email, password, role, { marketingOptIn, dateOfBirth });
      navigate(role === 'roommate_seeker' ? '/tenant/onboarding' : '/landlord/onboarding');
    } catch (err) {
      setError(getAuthErrorMessage(err, 'Failed to create account. Please try again.'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setError('');

    if (!dateOfBirth) {
      setError('Please enter your date of birth before continuing with Google.');
      return;
    }
    if (!isAdult(dateOfBirth)) {
      setError(`You must be at least ${MINIMUM_AGE} years old to use RoomZa.`);
      return;
    }
    if (!acceptedTerms) {
      setError('Please accept the Terms of Use and Community Guidelines to continue.');
      return;
    }

    setLoading(true);

    try {
      const persistedRole = await signupWithGoogle(role, { marketingOptIn, dateOfBirth });
      navigate(persistedRole === 'roommate_seeker' ? '/tenant/onboarding' : '/landlord/onboarding');
    } catch (err) {
      setError(getAuthErrorMessage(err, 'Failed to sign up with Google.'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans selection:bg-pink-200 selection:text-pink-900 scroll-smooth flex items-center justify-center px-4">
      <SeoHead
        title="Sign up"
        description="Create a free RoomZa account to find compatible roommates or list your property across South Africa."
        path="/signup"
      />
      <PageBackground />

      <div className="max-w-md w-full relative z-10">
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex items-center justify-center mb-3">
            <img src="/logo.png" alt="RoomZa Logo" className="w-12 h-12 rounded-xl" />
          </Link>
          <h1 className="text-4xl font-extrabold text-gray-900 mb-1">Join RoomZa</h1>
          <p className="text-text-secondary text-sm">Create your account to get started</p>
        </div>

        <div className="bg-white/60 backdrop-blur-md rounded-3xl p-6 border border-white/50 shadow-xl shadow-pink-100/50">
          {/* Role Selection */}
          <div className="mb-4">
            <span className="block text-sm font-semibold text-text-primary mb-2">
              I want to...
            </span>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setRole('roommate_seeker')}
                aria-pressed={role === 'roommate_seeker'}
                className={`py-3 px-3 rounded-xl border-2 transition-all font-semibold text-sm ${
                  role === 'roommate_seeker'
                    ? 'border-pink-500 bg-gradient-to-br from-pink-500 to-rose-600 text-white shadow-lg scale-105'
                    : 'border-gray-200 hover:border-pink-300 bg-white'
                }`}
              >
                <div className="text-xl mb-1">🤝</div>
                Find Roommate
              </button>
              <button
                type="button"
                onClick={() => setRole('landlord')}
                aria-pressed={role === 'landlord'}
                className={`py-3 px-3 rounded-xl border-2 transition-all font-semibold text-sm ${
                  role === 'landlord'
                    ? 'border-pink-500 bg-gradient-to-br from-pink-500 to-rose-600 text-white shadow-lg scale-105'
                    : 'border-gray-200 hover:border-pink-300 bg-white'
                }`}
              >
                <div className="text-xl mb-1">🏠</div>
                List Property
              </button>
            </div>
          </div>

          {error && (
            <div role="alert" className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl text-red-600 text-sm font-medium">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label htmlFor="email" className="block text-xs font-medium text-text-primary mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input text-sm h-11"
                placeholder="you@example.com"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="password" className="block text-xs font-medium text-text-primary mb-1">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input text-sm h-11"
                  placeholder="••••••••"
                  required
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-xs font-medium text-text-primary mb-1">
                  Confirm
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input text-sm h-11"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="dateOfBirth" className="block text-xs font-medium text-text-primary mb-1">
                Date of birth
              </label>
              <input
                id="dateOfBirth"
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className="input text-sm h-11"
                required
              />
              <p className="text-[11px] text-text-muted mt-1">You must be at least {MINIMUM_AGE} to use RoomZa.</p>
            </div>

            <label className="flex items-start gap-2 mt-4 text-xs text-text-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-0.5 h-4 w-4 flex-shrink-0 accent-pink-600"
              />
              <span>
                I agree to RoomZa&rsquo;s{' '}
                <Link to="/legal/terms" className="text-pink-600 font-medium underline underline-offset-2">
                  Terms of Use
                </Link>{' '}
                and{' '}
                <Link to="/legal/community-guidelines" className="text-pink-600 font-medium underline underline-offset-2">
                  Community Guidelines
                </Link>
                , and I have read the{' '}
                <Link to="/legal/privacy" className="text-pink-600 font-medium underline underline-offset-2">
                  Privacy Policy
                </Link>
                .
              </span>
            </label>

            <label className="flex items-start gap-2 text-xs text-text-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={marketingOptIn}
                onChange={(e) => setMarketingOptIn(e.target.checked)}
                className="mt-0.5 h-4 w-4 flex-shrink-0 accent-pink-600"
              />
              <span>Send me RoomZa news, tips, and offers by email. I can unsubscribe at any time.</span>
            </label>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 px-6 rounded-xl bg-gradient-to-r from-pink-500 to-rose-600 text-white font-bold hover:shadow-xl hover:shadow-pink-200 hover:scale-105 transition-all mt-2 disabled:opacity-60"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          <div className="mt-4">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border-light"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-white/60 text-text-muted">Or continue with</span>
              </div>
            </div>

            <button
              onClick={handleGoogleSignup}
              disabled={loading}
              className="w-full h-11 px-6 rounded-xl border-2 border-pink-100 bg-white text-pink-600 font-semibold text-sm hover:border-pink-200 hover:bg-pink-50 transition-all flex items-center justify-center mt-4"
            >
              Continue with Google
            </button>
          </div>

          <p className="mt-4 text-center text-text-secondary text-xs">
            Already have an account?{' '}
            <Link
              to="/login"
              className="text-pink-600 hover:text-pink-700 font-medium"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
